import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userService } from '../services/userService';
import { InviteUserRequest, TeamMember } from '../types/api';

interface InvitationsState {
  invitations: TeamMember[];
  loading: boolean;
  error: string | null;
  searchText: string;
  deleteInvitationLoading: boolean;
  deleteInvitationError: string | null;
  deleteInvitationSuccess: string | null;
  resendInvitationLoading: boolean;
  resendInvitationError: string | null;
  resendInvitationSuccess: string | null;
  shouldRefetch: boolean;
}

const initialState: InvitationsState = {
  invitations: [],
  loading: false,
  error: null,
  searchText: '',
  deleteInvitationLoading: false,
  deleteInvitationError: null,
  deleteInvitationSuccess: null,
  resendInvitationLoading: false,
  resendInvitationError: null,
  resendInvitationSuccess: null,
  shouldRefetch: false,
};

export const fetchInvitations = createAsyncThunk(
  'invitations/fetchInvitations',
  async (
    {
      searchText = '',
      tenantId,
    }: {
      searchText?: string;
      tenantId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await userService.getInvitations(searchText, tenantId);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch invitations';
      return rejectWithValue(errorMessage);
    }
  },
);

// Removing a pending invite is the same unified delete as removing an active
// member.
export const deleteInvitation = createAsyncThunk(
  'invitations/deleteInvitation',
  async (
    {
      uuid,
      tenantId,
    }: {
      uuid: string;
      tenantId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await userService.deleteUser(uuid, tenantId);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete invitation';
      return rejectWithValue(errorMessage);
    }
  },
);

export const inviteUser = createAsyncThunk(
  'invitations/inviteUser',
  async (request: InviteUserRequest, { rejectWithValue }) => {
    try {
      return await userService.inviteUser(request);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to invite user';
      return rejectWithValue(errorMessage);
    }
  },
);

// Resend re-issues the same idempotent invite for an already-pending member.
export const resendInvitation = createAsyncThunk(
  'invitations/resendInvitation',
  async (
    {
      email,
      roleName,
      ehrUsername,
      tenantId,
    }: {
      email: string;
      roleName: string;
      ehrUsername?: string;
      tenantId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      await userService.inviteUser({
        tenantId,
        email,
        roles: [roleName],
        ehrUsername: ehrUsername || undefined,
      });
      return { message: 'Invitation resent successfully' };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to resend invitation';
      return rejectWithValue(errorMessage);
    }
  },
);

const invitationsSlice = createSlice({
  name: 'invitations',
  initialState,
  reducers: {
    clearInvitations: (state) => {
      state.invitations = [];
      state.error = null;
      state.searchText = '';
    },
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    resetDeleteInvitationError: (state) => {
      state.deleteInvitationError = null;
    },
    resetDeleteInvitationSuccess: (state) => {
      state.deleteInvitationSuccess = null;
    },
    resetResendInvitationError: (state) => {
      state.resendInvitationError = null;
    },
    resetResendInvitationSuccess: (state) => {
      state.resendInvitationSuccess = null;
    },
    resetShouldRefetch: (state) => {
      state.shouldRefetch = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvitations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchInvitations.fulfilled,
        (state, action: PayloadAction<TeamMember[]>) => {
          state.loading = false;
          state.invitations = action.payload;
          state.shouldRefetch = false;
        },
      )
      .addCase(fetchInvitations.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || 'Failed to fetch invitations';
      })
      .addCase(deleteInvitation.pending, (state) => {
        state.deleteInvitationLoading = true;
        state.deleteInvitationError = null;
      })
      .addCase(deleteInvitation.fulfilled, (state, action) => {
        state.deleteInvitationLoading = false;
        state.deleteInvitationError = null;
        state.deleteInvitationSuccess = action.payload.message;
        state.invitations = [];
        state.shouldRefetch = true;
      })
      .addCase(deleteInvitation.rejected, (state, action) => {
        state.deleteInvitationLoading = false;
        state.deleteInvitationError =
          (action.payload as string) || 'Failed to delete invitation';
      })
      .addCase(inviteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(inviteUser.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.shouldRefetch = true;
      })
      .addCase(inviteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to invite user';
      })
      .addCase(resendInvitation.pending, (state) => {
        state.resendInvitationLoading = true;
        state.resendInvitationError = null;
        state.resendInvitationSuccess = null;
      })
      .addCase(resendInvitation.fulfilled, (state, action) => {
        state.resendInvitationLoading = false;
        state.resendInvitationError = null;
        state.resendInvitationSuccess = action.payload.message;
      })
      .addCase(resendInvitation.rejected, (state, action) => {
        state.resendInvitationLoading = false;
        state.resendInvitationError =
          (action.payload as string) || 'Failed to resend invitation';
        state.resendInvitationSuccess = null;
      });
  },
});

export const {
  clearInvitations,
  setSearchText,
  resetDeleteInvitationError,
  resetDeleteInvitationSuccess,
  resetResendInvitationError,
  resetResendInvitationSuccess,
  resetShouldRefetch,
} = invitationsSlice.actions;
export default invitationsSlice.reducer;
